import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'

import { useSpents } from 'app/audiences/application'
import { formatCurrency, formatDate } from 'app/common/presentation/format'

import { Chart, Section } from 'components'

import styles from './Overview.module.scss'
import SpentsModal from './SpentsModal'
import SpentsTable from './SpentsTable'

interface OverviewProps {
  audienceId: string
  className?: string
}

const maxShownTreatments = 6

const Overview = ({ audienceId, className }: OverviewProps) => {
  const { t } = useTranslation('audiences')
  const { daySpents, totalSpent, treatmentSpents } = useSpents(audienceId)

  const [showMoreTreatmentSpents, setShowMoreTreatmentSpents] = useState(false)

  return (
    <div className={[styles.details, className].join(' ')}>
      <Section name={t('details.overview.title')} />
      <div className={styles.overview}>
        <div className={styles.treatments}>
          <SpentsTable data={treatmentSpents.slice(0, maxShownTreatments)} />
          {treatmentSpents.length > maxShownTreatments && (
            <div className={styles.viewMore} onClick={() => setShowMoreTreatmentSpents(true)}>
              {t('details.overview.viewMore')}
            </div>
          )}
        </div>
        <div className={styles.chart}>
          <Chart.LineChart
            data={daySpents.map((entry) => ({ key: entry.date, values: [entry.spent] }))}
            formatTooltipLabel={(date) => format(date, 'dd-MM-yyyy')}
            formatXTick={formatDate}
            formatYTick={(value) => formatCurrency(value, { fractionDigits: 0 })}
            strokeWidth={3}
            yScaleFactor={1.5}
          />
          <div className={styles.chartInfo}>
            <div>
              <div>{t('details.overview.chart.total')}</div>
              <div>{t('details.overview.chart.days', { days: 7 })}</div>
            </div>
            <div>{formatCurrency(totalSpent, { fractionDigits: 0 })}</div>
          </div>
        </div>
      </div>
      <SpentsModal
        treatmentSpents={treatmentSpents}
        onClose={() => setShowMoreTreatmentSpents(false)}
        open={showMoreTreatmentSpents}
      />
    </div>
  )
}

export default Overview
